import { loadRemote, registerRemotes } from '@module-federation/runtime'

import { REMOTE_ENTRY_MODULE, REMOTE_MANIFEST_URL } from './constants.ts'
import type { RemoteAppModule, RemoteDefinition, RemoteManifest } from './types.ts'

/**
 * Реестр микрофронтов.
 *
 * Хост не знает про ремоуты на этапе сборки: список приезжает манифестом
 * и регистрируется в рантайме. Благодаря этому продуктовые команды катят
 * свои микрофронты независимо, а хост остаётся неизменным.
 */

export class RemoteUnavailableError extends Error {
  readonly remoteName: string

  constructor(remoteName: string, cause?: unknown) {
    super(`Микрофронт «${remoteName}» недоступен`)
    this.name = 'RemoteUnavailableError'
    this.remoteName = remoteName
    this.cause = cause
  }
}

const registered = new Map<string, RemoteDefinition>()

async function fetchManifest(url: string): Promise<RemoteDefinition[] | null> {
  try {
    const response = await fetch(url, { cache: 'no-cache' })
    if (!response.ok) return null

    const data = (await response.json()) as RemoteManifest
    return Array.isArray(data.remotes) ? data.remotes : null
  } catch {
    // Манифеста нет — это нормальное состояние на чистой машине разработчика.
    // Хост обязан подняться и без единого ремоута.
    return null
  }
}

export function registerRemoteDefinitions(definitions: RemoteDefinition[]): RemoteDefinition[] {
  const fresh = definitions.filter(
    (definition) => definition.name && definition.entry && !registered.has(definition.name),
  )

  if (fresh.length > 0) {
    // `type: 'module'` обязателен: @module-federation/vite отдаёт remoteEntry
    // ES-модулем и в dev, и в сборке, а рантайм по умолчанию ждёт классический
    // скрипт с глобальной переменной — и падает на первом же `import`.
    registerRemotes(fresh.map(({ name, entry }) => ({ name, entry, type: 'module' })))
    for (const definition of fresh) registered.set(definition.name, definition)
  }

  return [...registered.values()]
}

/**
 * Читает манифест и регистрирует всё, что в нём нашлось.
 *
 * @param fallback список из переменных окружения — им пользуются, когда
 * ремоут поднят локально и в общий манифест ещё не попал.
 */
export async function initRemotes(
  fallback: RemoteDefinition[] = [],
  manifestUrl: string = REMOTE_MANIFEST_URL,
): Promise<RemoteDefinition[]> {
  const fromManifest = await fetchManifest(manifestUrl)
  return registerRemoteDefinitions([...(fromManifest ?? []), ...fallback])
}

export function isRemoteRegistered(name: string): boolean {
  return registered.has(name)
}

export function getRegisteredRemotes(): RemoteDefinition[] {
  return [...registered.values()]
}

/**
 * Загружает корневой компонент микрофронта.
 *
 * Все отказы приводятся к `RemoteUnavailableError`: интерфейсу неважно,
 * не нашёлся ремоут в реестре, не ответил его сервер или он отдал модуль
 * без экспорта по умолчанию — во всех трёх случаях показывается одна
 * и та же заглушка с кнопкой «Повторить».
 */
export async function loadRemoteApp(
  name: string,
  module: string = REMOTE_ENTRY_MODULE,
): Promise<RemoteAppModule['default']> {
  if (!registered.has(name)) throw new RemoteUnavailableError(name)

  try {
    const loaded = await loadRemote<RemoteAppModule>(`${name}/${module.replace(/^\.\//, '')}`)
    if (!loaded?.default) throw new Error('Модуль не экспортирует компонент по умолчанию')
    return loaded.default
  } catch (error) {
    throw new RemoteUnavailableError(name, error)
  }
}
